import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', credentials);

      // Check if 2FA is required
      if (response.data.requiresTwoFactor) {
        // Return the 2FA response with temp token and userId
        return {
          requiresTwoFactor: true,
          tempToken: response.data.tempToken,
          userId: response.data.userId,
          message: response.data.message
        };
      }

      // Standard login response
      const { token, user } = response.data;
      if (token && user) {
        localStorage.setItem('token', token);
        return { token, user };
      } else {
        return rejectWithValue('Invalid response from server');
      }
    } catch (error) {
      // Handle 429 Rate Limit errors
      if (error.response?.status === 429 || error.isRateLimitError) {
        const rateLimitMessage = error.response?.data?.message || error.response?.data?.error?.error ||
          'Too many requests. Please wait a moment and try logging in again.';
        return rejectWithValue(rateLimitMessage);
      }
      // Handle network errors (server not running or CORS issues)
      if (!error.response && (error.message === 'Network Error' || error.code === 'ERR_NETWORK')) {
        return rejectWithValue('Cannot connect to server. Please ensure the backend server is running on port 5001.');
      }
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error?.error ||
        error.response?.data?.error ||
        error.message ||
        'Login failed';
      return rejectWithValue(errorMessage);
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/register', userData);
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      return { token, user };
    } catch (error) {
      const data = error.response?.data;
      if (data?.errors?.length) {
        return rejectWithValue(data.errors.map((e) => e.msg || e.message).filter(Boolean).join('. '));
      }
      const serverError =
        typeof data?.error === 'string'
          ? data.error
          : data?.error?.error || data?.error?.message;
      return rejectWithValue(data?.message || serverError || 'Registration failed');
    }
  }
);

// Track last getMe call time to prevent rate limiting
let lastGetMeCall = 0;
const GET_ME_THROTTLE_MS = 2000; // Minimum 2 seconds between calls

export const getMe = createAsyncThunk(
  'auth/getMe',
  async (_, { rejectWithValue }) => {
    try {
      // Throttle getMe calls to prevent rate limiting
      const now = Date.now();
      const timeSinceLastCall = now - lastGetMeCall;

      if (timeSinceLastCall < GET_ME_THROTTLE_MS) {
        // If called too soon, wait a bit
        await new Promise(resolve => setTimeout(resolve, GET_ME_THROTTLE_MS - timeSinceLastCall));
      }

      lastGetMeCall = Date.now();
      const response = await api.get('/auth/me');
      return response.data.user;
    } catch (error) {
      // Provide detailed error information for better handling
      const errorMessage = error.response?.data?.message || error.message || 'Failed to get user';
      const statusCode = error.response?.status;

      // Handle 429 Too Many Requests
      if (statusCode === 429) {
        console.warn('Rate limited on /auth/me. Waiting before retry...');
        // Wait a bit longer before allowing retry
        lastGetMeCall = Date.now() + 5000; // Add 5 seconds penalty
        return rejectWithValue('Too many requests. Please wait a moment and refresh.');
      }

      // Include status code in error for better error handling
      if (statusCode === 401) {
        return rejectWithValue('Unauthorized - Invalid or expired token');
      }

      return rejectWithValue(errorMessage);
    }
  }
);

export const logout = createAsyncThunk('auth/logout', async () => {
  localStorage.removeItem('token');
  localStorage.removeItem('impersonator_token');
  localStorage.removeItem('impersonator_user');
});

// Platform owner "login as" a tester for support/troubleshooting. Stashes the
// owner's own session under separate localStorage keys so endImpersonation
// can restore it — impersonation tokens are short-lived (2h) and heavily
// audited server-side (see authController.startImpersonation/endImpersonation).
export const startImpersonation = createAsyncThunk(
  'auth/startImpersonation',
  async (testerId, { getState, rejectWithValue }) => {
    try {
      const response = await api.post(`/auth/impersonate/${testerId}`);
      const { token, user, impersonatedBy } = response.data;

      const currentToken = localStorage.getItem('token');
      const currentUser = getState().auth.user;
      if (currentToken && currentUser) {
        localStorage.setItem('impersonator_token', currentToken);
        localStorage.setItem('impersonator_user', JSON.stringify(currentUser));
      }

      localStorage.setItem('token', token);
      return { token, user, impersonatedBy };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to start impersonation');
    }
  }
);

export const endImpersonation = createAsyncThunk(
  'auth/endImpersonation',
  async (_, { rejectWithValue }) => {
    try {
      await api.post('/auth/impersonate/end');
    } catch (error) {
      // Best-effort audit log — don't block restoring the owner's own session.
    }

    const originalToken = localStorage.getItem('impersonator_token');
    const originalUserRaw = localStorage.getItem('impersonator_user');
    if (!originalToken || !originalUserRaw) {
      return rejectWithValue('No impersonator session to restore');
    }

    let originalUser;
    try {
      originalUser = JSON.parse(originalUserRaw);
    } catch (error) {
      return rejectWithValue('Corrupt impersonator session');
    }

    localStorage.setItem('token', originalToken);
    localStorage.removeItem('impersonator_token');
    localStorage.removeItem('impersonator_user');

    return { token: originalToken, user: originalUser };
  }
);

const hasToken = localStorage.getItem('token');

// Restore the "impersonating as X" banner state across a page refresh — the
// impersonator's identity lives in a separate localStorage key from the
// active session token so it survives getMe() re-authenticating as the tester.
let initialImpersonatedBy = null;
try {
  const stashedUser = localStorage.getItem('impersonator_user');
  if (stashedUser) {
    const u = JSON.parse(stashedUser);
    initialImpersonatedBy = { id: u.id, email: u.email, firstName: u.firstName, lastName: u.lastName };
  }
} catch (error) {
  // Ignore corrupt stashed session — endImpersonation will no-op if there's nothing valid to restore.
}

const initialState = {
  user: null,
  token: hasToken,
  isAuthenticated: false,
  loading: false, // Start as false - getMe.pending will set it to true when called
  error: null,
  impersonatedBy: initialImpersonatedBy,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action) => {
      state.user = { ...state.user, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;

        // If 2FA is required, don't set authentication state yet
        if (action.payload.requiresTwoFactor) {
          // Just clear loading, don't set isAuthenticated
          // The Login component will handle OTP verification
          return;
        }

        // Standard login flow
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Me
      .addCase(getMe.pending, (state) => {
        state.loading = true;
        state.error = null; // Clear previous errors
      })
      .addCase(getMe.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;
        state.error = null; // Clear any errors on success
      })
      .addCase(getMe.rejected, (state, action) => {
        state.loading = false; // Always clear loading on error
        state.isAuthenticated = false;
        state.error = action.payload;

        // Check if this is an authentication error (401) or network error
        // The token will already be cleared by the axios interceptor on 401
        // We just need to update the state accordingly
        const token = localStorage.getItem('token');
        const errorMessage = String(action.payload || '');

        // Handle 429 Rate Limit - clear token immediately to prevent infinite retries
        if (errorMessage.includes('Too many requests') || errorMessage.includes('429') || errorMessage.includes('rate limit')) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Rate limited on /auth/me. Clearing token immediately to prevent infinite loop.');
          }
          localStorage.removeItem('token');
          state.token = null;
          state.error = 'Too many requests. Please wait a moment and try logging in again.';
          return; // Exit early to prevent further processing
        }

        if (!token) {
          // Token was cleared by interceptor, this was a 401 error
          state.token = null;
        } else {
          // For network errors - clear token to prevent infinite retry loop
          if (errorMessage.includes('Failed to fetch') ||
            errorMessage.includes('Network Error') ||
            errorMessage.includes('Cannot connect') ||
            errorMessage.includes('ERR_NETWORK')) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('Network error during authentication. Clearing token to prevent infinite loop.');
            }
            localStorage.removeItem('token');
            state.token = null;
            state.error = 'Cannot connect to server. Please check if the backend server is running on port 5001.';
          } else {
            // Token still exists, but we failed - clear it to allow fresh login
            if (process.env.NODE_ENV === 'development') {
              console.warn('Authentication verification failed. Clearing token to allow fresh login:', action.payload);
            }
            localStorage.removeItem('token');
            state.token = null;
          }
        }
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.impersonatedBy = null;
      })
      // Start impersonation
      .addCase(startImpersonation.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.impersonatedBy = action.payload.impersonatedBy;
        state.error = null;
      })
      .addCase(startImpersonation.rejected, (state, action) => {
        state.error = action.payload;
      })
      // End impersonation
      .addCase(endImpersonation.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.impersonatedBy = null;
        state.error = null;
      })
      .addCase(endImpersonation.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError, updateUser } = authSlice.actions;
export default authSlice.reducer;

